import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDayTime } from "@/lib/time";
import { Btn, CopyField, Kv, Panel, Warn } from "@/components/portal/crm/kit";
import { ConfirmDialog, mono } from "./ConfirmDialog";

/** The one key their system signs in with. Shown once; resetting kills the old one straight away. */
export const ApiKeyPanel = ({ info, onChange }) => {
  const [fresh, setFresh] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const key = info?.key;

  const make = async () => {
    setAsking(false); setBusy(true);
    try {
      const r = await api.post("/refer/api/key");
      setFresh(r.data.key);
      toast.success(key ? "New key made — the old one has stopped working" : "Your API key is ready");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't make a key"); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="Your API key" testId="api-key-panel"
           sub="Your system sends this in the X-API-Key header on every call. Keep it on your server, never in a web page or app."
           right={<Btn kind={key ? "ghost" : "primary"} sm disabled={busy} onClick={() => (key ? setAsking(true) : make())}
                       testId={key ? "api-key-reset" : "api-key-create"}>{key ? "Reset key" : "Create API key"}</Btn>}>
      {fresh && (
        <Warn testId="api-key-fresh" style={{ display: "block", marginBottom: 16 }}>
          <b>Copy it now — we only show it once.</b> We keep a fingerprint, not the key, so nobody at Connexis can read it back.
          <div style={{ marginTop: 12 }}><CopyField value={fresh} testId="api-key-fresh-value" /></div>
        </Warn>
      )}
      {key ? (
        <div data-testid="api-key-meta">
          <Kv rows={[
            ["Key", <span key="k" style={mono} data-testid="api-key-prefix">{key.prefix}</span>],
            ["Created", ukDayTime(key.created_at)],
            ["Last used", key.last_used_at ? ukDayTime(key.last_used_at) : "Not used yet"],
          ]} />
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--mut)" }} data-testid="api-key-none">
          No key yet. Create one and paste it into your system's settings.
        </p>
      )}
      <ConfirmDialog open={asking} onClose={() => setAsking(false)} onYes={make} yes="Reset the key" testId="api-key-reset-dialog"
                     title="Reset your API key?"
                     body="Your old key stops working straight away. Any system still using it will be turned away until you paste in the new one." />
    </Panel>
  );
};
